/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import CopyLinkDialog from "components/ui/CopyLinkDialog";
import useApi from "components/Utils/api";
import toast from "utils/toast";
import { encodeInvId } from "utils/invoiceCrypto";

const EMPTY_ITEM = () => ({ itemName: "", rate: "", quantity: "" });

const INITIAL_FORM = {
  billingCompany: "",
  billingAddress: "",
  sameAsBilling: false,
  shippingAddress: "",
  currency: "AED",
  items: [EMPTY_ITEM()],
  isScheduled: false,
  scheduleDate: "",
  scheduleTime: "00:00",
  hasExpiry: false,
  expiryDate: "",
  expiryTime: "00:00",
  notifyEmail: false,
  notifyEmailAddress: "",
  notifySms: false,
  notifyPhoneNumber: "",
  remarks: "",
  conditions: "",
  createPaymentLink: false,
  vatEnabled: false,
  taxPercent: "",
};

const NAV_KEYS = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];

const allowIntegerKeys = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey || NAV_KEYS.includes(e.key)) return;
  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
};

const allowDecimalKeys = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey || NAV_KEYS.includes(e.key)) return;
  if (e.key === "." && !e.target.value.includes(".")) return;
  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
};

const sanitizeDecimal = (raw) => {
  let value = raw.replace(/[^0-9.]/g, "");
  const firstDot = value.indexOf(".");
  if (firstDot !== -1) {
    value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "");
  }
  return value;
};

const sanitizeInteger = (raw) => raw.replace(/[^0-9]/g, "");

const pad2 = (n) => String(n).padStart(2, "0");

const getNowDateTimeParts = () => {
  const d = new Date();
  return {
    date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
    time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
  };
};

const isPastDateTime = (dateStr, timeStr) => {
  if (!dateStr) return false;
  const { date: todayStr, time: nowTimeStr } = getNowDateTimeParts();
  return `${dateStr}T${timeStr || "00:00"}` < `${todayStr}T${nowTimeStr}`;
};

function SectionHeader({ label, mt = 2.5 }) {
  return (
    <MDBox mt={mt} mb={0.5}>
      <MDTypography
        variant="caption"
        fontWeight="bold"
        color="info"
        sx={{ textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.72rem" }}
      >
        {label}
      </MDTypography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
    </MDBox>
  );
}

function NewInvoice() {
  const navigate = useNavigate();
  const api = useApi();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdLink, setCreatedLink] = useState("");
  const [showMore, setShowMore] = useState(false);
  const scheduleDateRef = useRef(null);
  const scheduleTimeRef = useRef(null);
  const expiryDateRef = useRef(null);
  const expiryTimeRef = useRef(null);

  useEffect(() => {
    api.get("customers")
      .then((res) => {
        const data = res.data?.data ?? [];
        const activeCustomers = data.filter(
          (c) => c.status === "ACTIVE" || c.status === true || c.status === 1 || c.status === "1"
        );
        setCustomers(activeCustomers);
      })
      .catch(() => {});
  }, []);

  const handleCustomerChange = (_, customer) => {
    setSelectedCustomer(customer);
    if (customer) {
      setForm((prev) => ({
        ...prev,
        billingCompany: customer.firstName + " " + customer.lastName || prev.billingCompany,
        billingAddress: customer.address || prev.billingAddress,
        shippingAddress: customer.address || prev.shippingAddress,
        notifyEmailAddress: customer.email || prev.notifyEmailAddress,
        notifyPhoneNumber: customer.phoneNumber || prev.notifyPhoneNumber,
      }));
    }
  };

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleScheduleDateChange = (e) => {
    const newStart = e.target.value;
    setForm((prev) => ({
      ...prev,
      scheduleDate: newStart,
      expiryDate: prev.expiryDate && prev.expiryDate < newStart ? "" : prev.expiryDate,
    }));
    setErrors((prev) => ({ ...prev, scheduleDate: false }));
  };

  const handleExpiryDateChange = (e) => {
    const newExpiry = e.target.value;
    setForm((prev) => ({
      ...prev,
      expiryDate: newExpiry,
      scheduleDate: prev.scheduleDate && prev.scheduleDate > newExpiry ? "" : prev.scheduleDate,
    }));
    setErrors((prev) => ({ ...prev, expiryDate: false }));
  };

  const toggle = (field) => (e) => {
    const checked = e.target.checked;
    setForm((prev) => {
      const next = { ...prev, [field]: checked };
      if (!checked && field === "notifyEmail") next.notifyEmailAddress = "";
      if (!checked && field === "notifySms") next.notifyPhoneNumber = "";
      return next;
    });
    if (!checked) {
      if (field === "isScheduled") setErrors((prev) => ({ ...prev, scheduleDate: false }));
      if (field === "hasExpiry") setErrors((prev) => ({ ...prev, expiryDate: false }));
      if (field === "notifyEmail") setErrors((prev) => ({ ...prev, notifyEmailAddress: false }));
      if (field === "notifySms") setErrors((prev) => ({ ...prev, notifyPhoneNumber: false }));
    }
  };

  const setItem = (idx, field, value) => {
    setForm((prev) => {
      const items = prev.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it));
      return { ...prev, items };
    });
    setErrors((prev) => {
      const itemErrors = (prev.items || []).map((e, i) =>
        i === idx ? { ...e, [field]: false } : e
      );
      return { ...prev, items: itemErrors };
    });
  };

  const addItem = () =>
    setForm((prev) => ({ ...prev, items: [...prev.items, EMPTY_ITEM()] }));

  const removeItem = (idx) =>
    setForm((prev) => ({
      ...prev,
      items: prev.items.length === 1 ? prev.items : prev.items.filter((_, i) => i !== idx),
    }));

  const getItemTotal = (it) =>
    parseFloat(it.rate || 0) * parseFloat(it.quantity || 0);

  const grandTotal = form.items.reduce((s, it) => s + getItemTotal(it), 0);
  const taxAmount = form.vatEnabled ? (grandTotal * parseFloat(form.taxPercent || 0)) / 100 : 0;

  const validate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {
      billingCompany: !form.billingCompany.trim(),
      currency: !form.currency.trim(),
      scheduleDate:
        form.isScheduled &&
        (!form.scheduleDate || isPastDateTime(form.scheduleDate, form.scheduleTime)),
      expiryDate:
        form.hasExpiry &&
        (!form.expiryDate || isPastDateTime(form.expiryDate, form.expiryTime)),
      taxPercent: form.vatEnabled && (!form.taxPercent || isNaN(parseFloat(form.taxPercent))),
      notifyEmailAddress:
        form.notifyEmail &&
        (!form.notifyEmailAddress.trim() || !emailRegex.test(form.notifyEmailAddress.trim())),
      notifyPhoneNumber: form.notifySms && !form.notifyPhoneNumber.trim(),
    };
    const itemErrors = form.items.map((it) => ({
      itemName: !it.itemName.trim(),
      rate: !it.rate || isNaN(parseFloat(it.rate)),
      quantity: !it.quantity || isNaN(parseInt(it.quantity, 10)),
    }));
    setErrors({ ...newErrors, items: itemErrors });
    return (
      !Object.values(newErrors).some(Boolean) &&
      !itemErrors.some((e) => Object.values(e).some(Boolean))
    );
  };

  const buildBody = (isDraft) => {
    const now = new Date();
    const total = form.items.reduce(
      (s, it) => s + parseFloat(it.rate || 0) * parseFloat(it.quantity || 0),
      0
    );
    const taxPct = form.vatEnabled ? parseFloat(form.taxPercent || 0) : 0;
    const taxAmt = (total * taxPct) / 100;

    const items = form.items.map((it) => ({
      itemName: it.itemName,
      rate: parseFloat(it.rate),
      quantity: parseInt(it.quantity, 10),
      total: parseFloat(it.rate) * parseInt(it.quantity, 10),
    }));

    const formatDate = (date, timeStr) => {
      const d = new Date(date);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const time = timeStr || `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      return `${dd}/${mm}/${yyyy} ${time}:00`;
    };

    const issueDate = form.isScheduled ? formatDate(form.scheduleDate, form.scheduleTime) : formatDate(now);
    const expiryDate = form.hasExpiry ? formatDate(form.expiryDate, form.expiryTime) : "";

    return {
      billingCompany: form.billingCompany,
      billingAddress: form.billingAddress,
      shippingAddress: form.sameAsBilling ? form.billingAddress : form.shippingAddress,
      issueDate,
      expiryDate,
      totalAmount: total,
      hasTax: form.vatEnabled ? 1 : 0,
      taxPercent: taxPct,
      taxAmount: taxAmt,
      remarks: form.remarks,
      conditions: form.conditions,
      currency: form.currency,
      isDraft,
      customerId: selectedCustomer?.customerId ?? null,
      customerName: selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : null,
      isScheduled: form.isScheduled,
      hasExpiry: form.hasExpiry,
      notifyViaEmail: !!form.notifyEmail,
      notifyViaSms: !!form.notifySms,
      notifiedEmail: form.notifyEmail ? form.notifyEmailAddress : null,
      notifiedNumber: form.notifySms ? form.notifyPhoneNumber : null,
      hasPaymentLink: !!form.createPaymentLink,
      items,
    };
  };

  const handleSave = (isDraft) => {
    if (!validate()) return;
    const body = buildBody(isDraft);
    setSaving(true);
    api.post("invoices", body)
      .then((res) => {
        toast.success(isDraft === 1 ? "Invoice saved as draft." : "Invoice finalized and issued.");
        const invoiceId = res.data?.id ?? res.data?.data?.id;
        if (invoiceId) {
          const portalBase = (process.env.REACT_APP_PORTAL_BASE_URL || "").replace(/\/$/, "");
          setCreatedLink(`${portalBase}/invoices/view/${encodeInvId(invoiceId)}`);
        } else {
          navigate("/invoices");
        }
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to save invoice.");
        }
      })
      .finally(() => setSaving(false));
  };

  const inputSx = { mb: 1.5 };
  const labelSx = { "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } };
  const { date: todayStr, time: nowTimeStr } = getNowDateTimeParts();
  const scheduleTimeMin = form.scheduleDate === todayStr ? nowTimeStr : undefined;
  const expiryTimeMins = [
    form.expiryDate === todayStr ? nowTimeStr : null,
    form.isScheduled && form.expiryDate && form.expiryDate === form.scheduleDate
      ? form.scheduleTime || "00:00"
      : null,
  ].filter(Boolean);
  const expiryTimeMin = expiryTimeMins.length
    ? expiryTimeMins.reduce((a, b) => (a > b ? a : b))
    : undefined;

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle="New Invoice" />
      <MDBox mt={3} mb={3}>
        <Grid container rowSpacing={0} columnSpacing={7} justifyContent="flex-start">
          <Grid item xs={12}>
            <MDBox mb={3} display="flex" alignItems="center">
              <MDBox flex={1}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  New Invoice
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  Create a new invoice
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="info" size="small" onClick={() => handleSave(1)} disabled={saving}>
                  {saving ? "Saving…" : "Save Draft"}
                </MDButton>
                <MDButton variant="gradient" color={sidenavColor} size="small" onClick={() => handleSave(0)} disabled={saving}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">add</Icon>
                  {saving ? "Saving…" : "Finalize & Issue"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox>
              {/* Customer selector */}
              <MDBox mb={0.5}>
                <Autocomplete
                  size="small"
                  options={customers || []}
                  getOptionLabel={(c) => `${c.firstName} ${c.lastName}`}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Customer (optional — prefills fields below)" />
                  )}
                />
              </MDBox>

              {/* Billing Info */}
              <SectionHeader label="Billing Info" />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing To"
                    fullWidth
                    size="small"
                    required
                    value={form.billingCompany}
                    onChange={set("billingCompany")}
                    error={!!errors.billingCompany}
                    helperText={errors.billingCompany ? "Billing Company is required" : ""}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Currency"
                    fullWidth
                    size="small"
                    required
                    value={form.currency}
                    onChange={set("currency")}
                    error={!!errors.currency}
                    helperText={errors.currency ? "Required" : ""}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing Address"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={form.billingAddress}
                    onChange={set("billingAddress")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox>
                    <TextField
                      label="Shipping Address"
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={form.sameAsBilling ? form.billingAddress : form.shippingAddress}
                      onChange={set("shippingAddress")}
                      disabled={form.sameAsBilling}
                      sx={{ mb: 0.5 }}
                    />
                    <MDBox display="flex" justifyContent="flex-end">
                      <FormControlLabel
                        sx={{
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.75rem",
                            fontWeight: 400,
                            color: "text.secondary",
                          },
                        }}
                        control={
                          <Checkbox
                            size="small"
                            checked={form.sameAsBilling}
                            onChange={toggle("sameAsBilling")}
                            sx={{ p: 0.25, color: "text.secondary" }}
                          />
                        }
                        label="Same as billing"
                      />
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>

              {/* Items */}
              <SectionHeader label="Items" />

              <Grid container spacing={1} sx={{ mb: 0.5, px: 0.5 }}>
                <Grid item xs={4.5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Item Name *</MDTypography>
                </Grid>
                <Grid item xs={2.5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Rate *</MDTypography>
                </Grid>
                <Grid item xs={2}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Qty *</MDTypography>
                </Grid>
                <Grid item xs={2.5}>
                  <MDTypography variant="caption" fontWeight="bold" color="text">Total</MDTypography>
                </Grid>
                <Grid item xs={0.5} />
              </Grid>

              {form.items.map((item, idx) => {
                const itTotal = getItemTotal(item);
                const itErrors = errors.items?.[idx] ?? {};
                return (
                  <Grid container spacing={1} alignItems="center" key={idx} sx={{ mb: 0.5 }}>
                    <Grid item xs={4.5}>
                      <TextField
                        placeholder="Item name"
                        fullWidth
                        size="small"
                        value={item.itemName}
                        onChange={(e) => setItem(idx, "itemName", e.target.value)}
                        error={!!itErrors.itemName}
                        helperText={itErrors.itemName ? "Required" : ""}
                      />
                    </Grid>
                    <Grid item xs={2.5}>
                      <TextField
                        placeholder="0.00"
                        type="text"
                        inputMode="decimal"
                        fullWidth
                        size="small"
                        value={item.rate}
                        onChange={(e) => setItem(idx, "rate", sanitizeDecimal(e.target.value))}
                        onKeyDown={allowDecimalKeys}
                        error={!!itErrors.rate}
                        helperText={itErrors.rate ? "Required" : ""}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        placeholder="1"
                        type="number"
                        fullWidth
                        size="small"
                        value={item.quantity}
                        onChange={(e) => setItem(idx, "quantity", sanitizeInteger(e.target.value))}
                        onKeyDown={allowIntegerKeys}
                        inputProps={{ min: 1 }}
                        error={!!itErrors.quantity}
                        helperText={itErrors.quantity ? "Required" : ""}
                      />
                    </Grid>
                    <Grid item xs={2.5}>
                      <TextField
                        value={itTotal > 0 ? itTotal.toFixed(2) : "0.00"}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        sx={{ "& .MuiInputBase-input": { color: "rgba(0,0,0,0.45)", cursor: "default" } }}
                      />
                    </Grid>
                    <Grid item xs={0.5} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeItem(idx)}
                        disabled={form.items.length === 1}
                        sx={{ p: "4px" }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}

              <MDBox display="flex" alignItems="center" justifyContent="space-between" mt={1.5} mb={0.5}>
                <MDButton size="small" variant="outlined" color="info" onClick={addItem} startIcon={<AddIcon />}>
                  Add Item
                </MDButton>
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 0.6,
                    borderRadius: "6px",
                    backgroundColor: "rgba(26,115,232,0.07)",
                    border: "1px solid rgba(26,115,232,0.2)",
                  }}
                >
                  <MDTypography variant="button" fontWeight="bold" sx={{ textTransform: "none", fontSize: "0.82rem" }}>
                    Grand Total:
                  </MDTypography>
                  <MDTypography variant="button" color="info" fontWeight="bold" sx={{ textTransform: "none", fontSize: "0.9rem" }}>
                    {grandTotal.toFixed(2)}
                  </MDTypography>
                  {form.vatEnabled && taxAmount > 0 && (
                    <MDTypography variant="caption" color="text" sx={{ ml: 0.5 }}>
                      + {taxAmount.toFixed(2)} tax
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              {/* Remarks & Terms (optional) */}
              <MDBox
                onClick={() => setShowMore((prev) => !prev)}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 4, cursor: "pointer", userSelect: "none" }}
              >
                <Icon fontSize="small" color="info">
                  {showMore ? "expand_less" : "expand_more"}
                </Icon>
                <MDTypography variant="button" color="info" fontWeight="medium">
                  Remarks and Terms & Conditions (Optional)
                </MDTypography>
              </MDBox>
              <Collapse in={showMore} timeout={300} unmountOnExit>
                <MDBox display="flex" gap={2} mt={1.5}>
                  <MDBox sx={{ flex: 1 }}>
                    <TextField
                      label="Remarks"
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      value={form.remarks}
                      onChange={set("remarks")}
                    />
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                      Only visible to you
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ flex: 1 }}>
                    <TextField
                      label="Terms & Conditions"
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      value={form.conditions}
                      onChange={set("conditions")}
                    />
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                      Visible at the end of invoice
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Collapse>
            </MDBox>
          </Grid>

          <Grid item xs={12} lg={6}>
            <MDBox
              sx={{
                borderLeft: { xs: "none", lg: "1px solid #e0e0e0" },
                pl: { xs: 0, lg: 5 },
              }}
            >
              {/* Options */}
              <SectionHeader label="Options" mt={-1} />

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.isScheduled} onChange={toggle("isScheduled")} />}
                    label="Schedule Invoice"
                  />
                  <Collapse in={form.isScheduled} timeout={300} unmountOnExit>
                    <MDBox display="flex" gap={1} mt={0.5} mb={1}>
                      <TextField
                        label="Schedule Date"
                        type="date"
                        size="small"
                        value={form.scheduleDate}
                        onChange={handleScheduleDateChange}
                        onClick={() => scheduleDateRef.current?.showPicker?.()}
                        inputRef={scheduleDateRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: todayStr,
                          max: form.expiryDate || undefined,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                          style: { cursor: "pointer", caretColor: "transparent" },
                        }}
                        error={!!errors.scheduleDate}
                        helperText={
                          errors.scheduleDate
                            ? form.scheduleDate
                              ? "Schedule date/time cannot be in the past"
                              : "Schedule date is required"
                            : ""
                        }
                        sx={{ flex: 1, cursor: "pointer" }}
                      />
                      <TextField
                        label="Schedule Time"
                        type="time"
                        size="small"
                        value={form.scheduleTime}
                        onChange={(e) => setForm((prev) => ({ ...prev, scheduleTime: e.target.value }))}
                        inputRef={scheduleTimeRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: scheduleTimeMin }}
                        error={!!errors.scheduleDate}
                        sx={{ flex: 1 }}
                      />
                    </MDBox>
                  </Collapse>
                </MDBox>

                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.hasExpiry} onChange={toggle("hasExpiry")} />}
                    label="Expiry"
                  />
                  <Collapse in={form.hasExpiry} timeout={300} unmountOnExit>
                    <MDBox display="flex" gap={1} mt={0.5} mb={1}>
                      <TextField
                        label="Expiry Date"
                        type="date"
                        size="small"
                        value={form.expiryDate}
                        onChange={handleExpiryDateChange}
                        onClick={() => expiryDateRef.current?.showPicker?.()}
                        inputRef={expiryDateRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: form.scheduleDate || todayStr,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                          style: { cursor: "pointer", caretColor: "transparent" },
                        }}
                        error={!!errors.expiryDate}
                        helperText={
                          errors.expiryDate
                            ? form.expiryDate
                              ? "Expiry date/time cannot be in the past"
                              : "Expiry date is required"
                            : ""
                        }
                        sx={{ flex: 1, cursor: "pointer" }}
                      />
                      <TextField
                        label="Expiry Time"
                        type="time"
                        size="small"
                        value={form.expiryTime}
                        onChange={(e) => setForm((prev) => ({ ...prev, expiryTime: e.target.value }))}
                        inputRef={expiryTimeRef}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: expiryTimeMin }}
                        error={!!errors.expiryDate}
                        sx={{ flex: 1 }}
                      />
                    </MDBox>
                  </Collapse>
                </MDBox>
              </MDBox>

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.notifyEmail} onChange={toggle("notifyEmail")} />}
                    label="Notify via Email"
                  />
                  <Collapse in={form.notifyEmail} timeout={300} unmountOnExit>
                    <TextField
                      label="Email Address"
                      fullWidth
                      size="small"
                      value={form.notifyEmailAddress}
                      onChange={set("notifyEmailAddress")}
                      error={!!errors.notifyEmailAddress}
                      helperText={errors.notifyEmailAddress ? "Valid email address is required" : ""}
                      sx={{ mt: 0.5, mb: 1 }}
                    />
                  </Collapse>
                </MDBox>
              </MDBox>

              {/* Settings */}
              <SectionHeader label="Settings" />

              <MDBox display="flex" gap={2} alignItems="flex-start">
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.createPaymentLink} onChange={toggle("createPaymentLink")} />}
                    label="Create Payment Link"
                  />
                </MDBox>
                <MDBox flex={1}>
                  <FormControlLabel
                    sx={labelSx}
                    control={<Checkbox size="small" checked={form.vatEnabled} onChange={toggle("vatEnabled")} />}
                    label="VAT"
                  />
                  {form.vatEnabled && (
                    <TextField
                      label="Tax %"
                      type="text"
                      inputMode="decimal"
                      size="small"
                      fullWidth
                      value={form.taxPercent}
                      onChange={(e) => set("taxPercent")({ target: { value: sanitizeDecimal(e.target.value) } })}
                      onKeyDown={allowDecimalKeys}
                      error={!!errors.taxPercent}
                      helperText={errors.taxPercent ? "Valid tax percentage is required" : ""}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <CopyLinkDialog
        open={!!createdLink}
        title="Invoice Created"
        message="Your invoice is ready. Copy the link to share it with your customer."
        link={createdLink}
        copyLabel="Copy Invoice Link"
        successMessage="Invoice link copied!"
        onClose={() => navigate("/invoices")}
      />
    </DashboardLayout>
  );
}

export default NewInvoice;
